import { z } from "zod";
import { commonSchemas } from "./common";
import type { ValidationT } from "./i18n";

/**
 * Zod schema for the top-nav login form.
 *
 * Factory form (R6 schema-factory): takes a `validation`-namespace translator so
 * error messages resolve through next-intl. Module-scope schemas can't call
 * useTranslations, so the consumer builds this via
 * useMemo(() => makeTopNavLoginSchema(vt), [vt]).
 */
export const makeTopNavLoginSchema = (t: ValidationT) =>
  z.object({
    nickname: z
      .string()
      .min(1, t("usernameRequired"))
      .max(50, t("usernameMax50")),
    wallet_address: commonSchemas.walletAddress,
  });

export type TopNavLoginValues = z.infer<
  ReturnType<typeof makeTopNavLoginSchema>
>;

export const makePasswordSecondFactorSchema = (t: ValidationT) =>
  z.object({
    password: z
      .string()
      .min(4, t("passwordMin4"))
      .max(128, t("passwordMax128")),
  });

export type PasswordSecondFactorValues = z.infer<
  ReturnType<typeof makePasswordSecondFactorSchema>
>;

/**
 * Schema for the profile Security page's "second-factor password" settings form.
 *
 * `requireCurrent` switches between the "set first password" and "change existing
 * password" flows — when the second factor is already enabled, the current
 * password is required to authorize the change.
 */
export const makePasswordSecondFactorSettingsSchema = (
  t: ValidationT,
  requireCurrent: boolean,
) =>
  z
    .object({
      current_password: requireCurrent
        ? z
            .string()
            .min(1, t("currentPasswordRequired"))
            .max(128, t("passwordMax128"))
        : z.string().max(128, t("passwordMax128")).optional(),
      new_password: z
        .string()
        .min(4, t("passwordMin4"))
        .max(128, t("passwordMax128")),
      confirm_password: z
        .string()
        .min(4, t("passwordMin4"))
        .max(128, t("passwordMax128")),
    })
    .refine((data) => data.new_password === data.confirm_password, {
      path: ["confirm_password"],
      message: t("passwordMismatch"),
    });

export type PasswordSecondFactorSettingsValues = z.infer<
  ReturnType<typeof makePasswordSecondFactorSettingsSchema>
>;
